import type { TimeSpan } from "api";
import { type ClassValue, clsx } from "clsx";
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { twMerge } from "tailwind-merge";

const toUtcIsoString = (date: Date) => date.toISOString();

export function getDateRangeFromTimeSpan(
  timeSpan: TimeSpan,
  dateRange?: { from?: Date; to?: Date },
): [string | undefined, string | undefined] {
  if (timeSpan === "custom") {
    return formatDateRangeForApi(dateRange);
  }
  const now = new Date();
  let start: Date;
  let end: Date;
  if (timeSpan === "day") {
    start = startOfDay(now);
    end = endOfDay(now);
  } else if (timeSpan === "week") {
    start = startOfWeek(now, { weekStartsOn: 1 });
    end = endOfWeek(now, { weekStartsOn: 1 });
  } else {
    start = startOfMonth(now);
    end = endOfMonth(now);
  }
  return [toUtcIsoString(start), toUtcIsoString(end)];
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function handleDownloadFromUrl(url: string) {
  window.open(url, "_blank");
}

export function formatCurrency(amount?: number, currencyCode = "€") {
  if (amount === undefined) return `0.00 ${currencyCode}`;
  return `${amount.toFixed(2)} ${currencyCode}`;
}

export function getInitials(str?: string) {
  if (!str) return "??";
  const parts = str.trim().split(" ");
  return parts.map((part) => part.substring(0, 2).toUpperCase()).join("");
}

export function formatShiftTime(timeValue: object | string | null): string {
  if (!timeValue) return "N/A";
  try {
    let date: Date;
    if (typeof timeValue === "string") {
      date = new Date(timeValue);
    } else if (typeof timeValue === "object" && "_seconds" in timeValue) {
      date = new Date((timeValue as { _seconds: number })._seconds * 1000);
    } else {
      return "Invalid Date";
    }
    return format(date, "MMM dd, yyyy HH:mm");
  } catch {
    return "Invalid Date";
  }
}

export function formatToDatetimeLocal(
  timeValue: string | object | null,
): string {
  if (!timeValue) return "";
  try {
    let date: Date;
    if (typeof timeValue === "string") {
      date = new Date(timeValue);
    } else if (typeof timeValue === "object" && "_seconds" in timeValue) {
      date = new Date((timeValue as { _seconds: number })._seconds * 1000);
    } else {
      return "";
    }
    return format(date, "yyyy-MM-dd'T'HH:mm");
  } catch {
    return "";
  }
}

export function calculateHoursBetween(
  clockIn: string,
  clockOut: string,
): string | null {
  if (!clockIn || !clockOut) return null;
  try {
    const start = new Date(clockIn);
    const end = new Date(clockOut);
    if (end <= start) return null;
    const diffMs = end.getTime() - start.getTime();
    const hours = diffMs / (1000 * 60 * 60);
    return hours.toFixed(2);
  } catch {
    return null;
  }
}

export function formatDateRangeForApi(
  dateRange:
    | {
        from?: Date;
        to?: Date;
      }
    | undefined,
): [string | undefined, string | undefined] {
  if (!dateRange?.from || !dateRange?.to) {
    return [undefined, undefined];
  }
  const startDate = new Date(dateRange.from);
  const endDate = new Date(dateRange.to);
  if (endDate.getHours() === 0 && endDate.getMinutes() === 0) {
    endDate.setHours(23, 59, 59, 999);
  }
  return [toUtcIsoString(startDate), toUtcIsoString(endDate)];
}
